import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WidgetScreen } from "../../common/WidgetScreen";

import { WidgetOverviewSettings } from "../WidgetOverviewSettings";
import { useWidgetChecklistOverviewPreviewQuery } from "./useWidgetChecklistOverviewPreviewQuery";
import { WidgetChecklistOverviewPreview } from "./WidgetChecklistOverviewPreview";
import { WSChecklistOverviewMutation } from "./__generated__/WSChecklistOverviewMutation.graphql";
import { WSChecklistOverviewQuery } from "./__generated__/WSChecklistOverviewQuery.graphql";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";

export type WSChecklistOverviewParams = object;

const WS_CHECKLIST_OVERVIEW_QUERY = graphql`
  query WSChecklistOverviewQuery($id: ID!) {
    store {
      ...WidgetOverviewSettings_Store
      publicTeams {
        id
      }
    }
    widgetRoutineOverview(id: $id) {
      id
      teams {
        id
      }
    }
  }
`;

export const WSChecklistOverview = withWebModal(WSChecklistOverviewC, true);
function WSChecklistOverviewC(
  props: UserLoggedInNavigatorScreenProps<"WSChecklistOverview">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerWSChecklistOverview {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWSChecklistOverview(
  props: UserLoggedInNavigatorScreenProps<"WSChecklistOverview"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WSChecklistOverviewQuery>(
    WS_CHECKLIST_OVERVIEW_QUERY,
    {
      id: props.route.params.widgetId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [teamIds, _setTeamIds] = useState<string[]>(
    (data.widgetRoutineOverview?.teams.length ?? 0) ===
      data.store.publicTeams.length
      ? []
      : (data.widgetRoutineOverview?.teams.map(({ id }) => id) ?? [])
  );

  const [save, loading] = useMutation<WSChecklistOverviewMutation>(graphql`
    mutation WSChecklistOverviewMutation(
      $storecastId: ID!
      $widgetId: ID!
      $update: WidgetRoutineOverviewUpdateInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        updateWidgetRoutineOverview(id: $widgetId, update: $update) {
          id
          ...WidgetRoutineOverviewDataComponent_widgetRoutineOverview
          teams {
            id
            name
          }
        }
      }
    }
  `);

  const onSave = useCallback(() => {
    save({
      variables: {
        storecastId: props.route.params.storecastId,
        update: {
          setTeamIds: teamIds,
        },
        widgetId: props.route.params.widgetId,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [
    props.navigation,
    props.route.params.storecastId,
    props.route.params.widgetId,
    save,
    teamIds,
  ]);

  const [query, setTeamIds] = useWidgetChecklistOverviewPreviewQuery({
    storecastId: props.route.params.storecastId,
    teamIds,
    setTeamIds: _setTeamIds,
  });

  return (
    <WidgetScreen
      settings={
        <WidgetOverviewSettings
          store={data.store}
          setTeamIds={setTeamIds}
          teamIds={teamIds}
        />
      }
      preview={
        <WidgetScreenPreview
          onSave={onSave}
          loading={loading}
          size={props.route.params.size}
          unitSize={props.route.params.unitSize}
        >
          {query ? (
            <WidgetChecklistOverviewPreview
              query={query}
              storecastId={props.route.params.storecastId}
            />
          ) : null}
        </WidgetScreenPreview>
      }
    />
  );
}
